import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import "react-phone-number-input/style.css";
import { Doughnut, Line } from "react-chartjs-2";
import { Chart, ArcElement, Tooltip, Legend, CategoryScale, LinearScale, PointElement, LineElement } from "chart.js";
Chart.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, PointElement, LineElement);

import {
  Card,
  Container,
  Row,
  Col,
  Table,
  Button,
  Badge,
  Form,
} from "react-bootstrap";
import { useAuth0 } from "@auth0/auth0-react";
import axios from "../../utils/axiosConfig";
import Preloader from "../../components/preloader";
import SitewideDataContext from "../../utils/sitewideContext";
import "./AdminData.css";

const ADMIN_DATA_URL = "https://api2.watchmypage.com/admin-data";

function AdminData() {
  const { setSitewideData } = useContext(SitewideDataContext);
  const { getAccessTokenSilently } = useAuth0();
  const [rawData, setRawData] = useState(null);
  const [uncrawledSites, setUncrawledSites] = useState([]);
  const [uncrawledSitesCount, setUncrawledSitesCount] = useState(0);
  const [crawledSitesCount, setCrawledSitesCount] = useState(0);
  const [inactiveSitesCount, setInactiveSitesCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastFetched, setLastFetched] = useState(null);
  const [timeSeriesSiteId, setTimeSeriesSiteId] = useState("__all__");
  const [refreshing, setRefreshing] = useState(false);

  const fetchData = useCallback(async (isBackgroundRefresh = false) => {
    setError(null);
    if (isBackgroundRefresh) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }
    try {
      const accessToken = await getAccessTokenSilently();
      const result = await axios.get(ADMIN_DATA_URL, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      const data = result.data;
      setRawData(data);
      setLastFetched(new Date());

      const sites = data?.sites ?? [];
      const uncrawled = sites.filter((s) => s.active && s.crawled_today === false);
      const crawled = sites.filter((s) => s.active && s.crawled_today === true);
      const inactive = sites.filter((s) => s.active === false);

      setUncrawledSitesCount(uncrawled.length);
      setCrawledSitesCount(crawled.length);
      setInactiveSitesCount(inactive.length);
      setUncrawledSites(uncrawled);
      const seriesIds = Object.keys(data?.crawls_time_series_by_site ?? {});
      if (seriesIds.length > 0) {
        setTimeSeriesSiteId((prev) => (prev === "__all__" || seriesIds.includes(prev) ? prev : "__all__"));
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || "Failed to load admin data");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [getAccessTokenSilently]);

  // Clear global loading overlay when this route is shown (Dashboard isn’t mounted on /admindata)
  useEffect(() => {
    setSitewideData((prev) => ({ ...prev, overloading: false }));
  }, [setSitewideData]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // By-owner summary: { owner -> { total, uncrawled } }
  const byOwner = useMemo(() => {
    if (!rawData?.sites) return [];
    const map = {};
    rawData.sites.forEach((s) => {
      const owner = s.owner || "(no owner)";
      if (!map[owner]) map[owner] = { total: 0, uncrawled: 0, crawled: 0, inactive: 0 };
      map[owner].total += 1;
      if (!s.active) map[owner].inactive += 1;
      else if (s.crawled_today) map[owner].crawled += 1;
      else map[owner].uncrawled += 1;
    });
    return Object.entries(map).map(([owner, counts]) => ({ owner, ...counts })).sort((a, b) => b.total - a.total);
  }, [rawData]);

  // Sites never crawled (last_crawl null or 0)
  const neverCrawled = useMemo(
    () => (rawData?.sites ?? []).filter((s) => s.last_crawl == null || s.last_crawl === 0),
    [rawData]
  );

  const ALL_SITES_VALUE = "__all__";

  // Time series for selected site (or all sites aggregated) -> chart data
  const timeSeriesChartData = useMemo(() => {
    const bySite = rawData?.crawls_time_series_by_site ?? {};
    if (timeSeriesSiteId === ALL_SITES_VALUE) {
      // Aggregate all sites by day: dayKey -> total pagecount
      const byDay = {};
      Object.values(bySite).forEach((series) => {
        if (!Array.isArray(series)) return;
        series.forEach((p) => {
          if (p.start_time == null) return;
          const dayKey = new Date(p.start_time * 1000).toDateString();
          byDay[dayKey] = (byDay[dayKey] || 0) + (p.pagecount || 0);
        });
      });
      const days = Object.keys(byDay).sort((a, b) => new Date(a) - new Date(b));
      if (days.length === 0) return null;
      return {
        labels: days,
        datasets: [{
          label: "All sites (total pages)",
          data: days.map((d) => byDay[d]),
          borderColor: "rgb(54, 162, 235)",
          tension: 0.2,
        }],
      };
    }
    const series = bySite[timeSeriesSiteId];
    if (!series || !Array.isArray(series) || series.length === 0) return null;
    const site = (rawData?.sites ?? []).find((s) => s.site_id === timeSeriesSiteId);
    const label = site ? `${site.site_name || site.site_url} (pages)` : timeSeriesSiteId;
    return {
      labels: series.map((p) => (p.start_time ? new Date(p.start_time * 1000).toLocaleDateString() : "")).reverse(),
      datasets: [{
        label,
        data: series.map((p) => p.pagecount).reverse(),
        borderColor: "rgb(54, 162, 235)",
        tension: 0.2,
      }],
    };
  }, [rawData, timeSeriesSiteId]);

  const siteIdToName = useMemo(() => {
    const map = {};
    (rawData?.sites ?? []).forEach((s) => { map[s.site_id] = s.site_name || s.site_url || s.site_id; });
    return map;
  }, [rawData]);

  const timeSeriesSiteIds = useMemo(
    () => Object.keys(rawData?.crawls_time_series_by_site ?? {}),
    [rawData]
  );

  // Daily stats across all sites (for summary charts and table)
  const dailyStatsAllSites = useMemo(() => {
    const bySite = rawData?.crawls_time_series_by_site ?? {};
    const byDay = {};
    Object.entries(bySite).forEach(([siteId, series]) => {
      if (!Array.isArray(series)) return;
      series.forEach((p) => {
        if (p.start_time == null) return;
        const dayKey = new Date(p.start_time * 1000).toDateString();
        if (!byDay[dayKey]) byDay[dayKey] = { totalPages: 0, siteIds: new Set() };
        byDay[dayKey].totalPages += p.pagecount || 0;
        byDay[dayKey].siteIds.add(siteId);
      });
    });
    return Object.entries(byDay)
      .map(([date, { totalPages, siteIds }]) => ({
        date,
        totalPages,
        sitesCrawled: siteIds.size,
        avgPagesPerSite: siteIds.size ? Math.round((totalPages / siteIds.size) * 10) / 10 : 0,
      }))
      .sort((a, b) => new Date(a.date) - new Date(b.date));
  }, [rawData]);

  // Full-page loader only on initial load (no data yet); refresh keeps content visible
  if (loading && !rawData) {
    return (
      <Container fluid className="wmp-fade-in">
        <div className="text-center py-5">
          <Preloader inline overlay={false} text textOverride="Loading admin data..." />
        </div>
      </Container>
    );
  }

  if (error) {
    return (
      <Container fluid className="wmp-fade-in">
        <Card>
          <Card.Body className="error-state text-center py-5 px-4">
            <p className="text-danger mb-2">{error}</p>
            <p className="text-muted small mb-3">
              This page may require admin access. If you have access, the API may be unavailable or rejecting the request (e.g. 403).
            </p>
            <Button variant="primary" onClick={fetchData}>
              Retry
            </Button>
          </Card.Body>
        </Card>
      </Container>
    );
  }

  const totalSites = uncrawledSitesCount + crawledSitesCount + inactiveSitesCount;

  return (
    <Container fluid className={`wmp-fade-in ${refreshing ? "admin-data-refreshing" : ""}`}>
      {/* Summary stats + Refresh */}
      <Row className="mb-3 align-items-center">
        <Col md={9}>
          <Row>
            <Col md={3}>
              <Card className="bg-primary text-white">
                <Card.Body className="py-3">
                  <div className="small text-white-50">Total sites</div>
                  <h4 className="mb-0">{totalSites}</h4>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3}>
              <Card className="bg-success text-white">
                <Card.Body className="py-3">
                  <div className="small text-white-50">Crawled today</div>
                  <h4 className="mb-0">{crawledSitesCount}</h4>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3}>
              <Card className="bg-warning text-dark">
                <Card.Body className="py-3">
                  <div className="small text-muted">Uncrawled (active)</div>
                  <h4 className="mb-0">{uncrawledSitesCount}</h4>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3}>
              <Card className="bg-secondary text-white">
                <Card.Body className="py-3">
                  <div className="small text-white-50">Inactive</div>
                  <h4 className="mb-0">{inactiveSitesCount}</h4>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Col>
        <Col md={3} className="text-md-end">
          <Button
            variant="primary"
            size="sm"
            className="admin-data-refresh-btn"
            onClick={() => fetchData(true)}
            disabled={refreshing}
          >
            {refreshing ? (
              <>
                <i className="fas fa-sync-alt fa-spin" /> Refreshing…
              </>
            ) : (
              <>
                <i className="fas fa-sync-alt" /> Refresh
              </>
            )}
          </Button>
          <div className="text-muted small mt-1">
            {lastFetched ? lastFetched.toLocaleTimeString() : "—"}
          </div>
        </Col>
      </Row>

      <Row>
        <Col lg={6}>
          <Card>
            <Card.Header>
              <Card.Title as="h4">Daily Crawl Status</Card.Title>
            </Card.Header>
            <Card.Body className="text-center">
              <Doughnut
                data={{
                  labels: ["Completed", "Uncrawled", "Inactive"],
                  datasets: [{
                    data: [crawledSitesCount, uncrawledSitesCount, inactiveSitesCount],
                    backgroundColor: ["rgb(54, 162, 235)", "rgb(255, 99, 132)", "rgb(255, 205, 86)"],
                    hoverOffset: 4,
                  }],
                }}
              />
            </Card.Body>
            <Card.Footer className="text-muted small">
              <i className="fas fa-clock" /> Last updated{" "}
              {lastFetched ? lastFetched.toLocaleTimeString() : "—"}
            </Card.Footer>
          </Card>
        </Col>
        <Col lg={6}>
          <Card>
            <Card.Header>
              <Card.Title as="h4">Uncrawled Sites</Card.Title>
              {uncrawledSites.length > 0 && (
                <Badge variant="warning" className="ml-2">{uncrawledSites.length}</Badge>
              )}
            </Card.Header>
            <Card.Body className="p-0">
              <Table responsive size="sm" className="mb-0">
                <thead>
                  <tr>
                    <th>Site</th>
                    <th>Owner</th>
                    <th>Days</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {uncrawledSites.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="text-center text-muted py-3">
                        All active sites crawled today.
                      </td>
                    </tr>
                  ) : (
                    uncrawledSites.map((site, index) => (
                      <tr key={site.site_id || index}>
                        <td>{site.site_url}</td>
                        <td>{site.owner}</td>
                        <td>{site.days_dead ?? "—"}</td>
                        <td><a href="#"><i className="fas fa-plus-circle" /></a></td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            </Card.Body>
            <Card.Footer className="text-muted small">
              <i className="fas fa-clock" /> Last updated{" "}
              {lastFetched ? lastFetched.toLocaleTimeString() : "—"}
            </Card.Footer>
          </Card>
        </Col>
      </Row>

      {/* By owner */}
      <Row>
        <Col>
          <Card className="mb-3">
            <Card.Header>
              <Card.Title as="h5" className="mb-0">By Owner</Card.Title>
            </Card.Header>
            <Card.Body className="p-0">
              <Table responsive size="sm" className="mb-0">
                <thead>
                  <tr>
                    <th>Owner</th>
                    <th>Total</th>
                    <th>Crawled today</th>
                    <th>Uncrawled</th>
                    <th>Inactive</th>
                  </tr>
                </thead>
                <tbody>
                  {byOwner.map(({ owner, total, crawled, uncrawled, inactive }) => (
                    <tr key={owner}>
                      <td>{owner}</td>
                      <td>{total}</td>
                      <td>{crawled}</td>
                      <td>{uncrawled}</td>
                      <td>{inactive}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Sites never crawled */}
      {neverCrawled.length > 0 && (
        <Row>
          <Col>
            <Card className="mb-3">
              <Card.Header>
                <Card.Title as="h5" className="mb-0">Sites Never Crawled</Card.Title>
              </Card.Header>
              <Card.Body className="p-0">
                <Table responsive size="sm" className="mb-0">
                  <thead>
                    <tr>
                      <th>Site</th>
                      <th>Owner</th>
                    </tr>
                  </thead>
                  <tbody>
                    {neverCrawled.map((s) => (
                      <tr key={s.site_id}>
                        <td>{s.site_url || s.site_name || s.site_id}</td>
                        <td>{s.owner || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}

      {/* Crawl activity (30 days) - summary table (all sites) + charts */}
      {timeSeriesSiteIds.length > 0 && (
        <>
          {/* Summary table: all sites by day */}
          {dailyStatsAllSites.length > 0 && (
            <Row>
              <Col>
                <Card className="mb-3">
                  <Card.Header>
                    <Card.Title as="h5" className="mb-0">Crawl Activity by Day (All Sites)</Card.Title>
                  </Card.Header>
                  <Card.Body className="p-0">
                    <Table responsive size="sm" className="mb-0">
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Total pages</th>
                          <th>Sites crawled</th>
                          <th>Avg pages/site</th>
                        </tr>
                      </thead>
                      <tbody>
                        {dailyStatsAllSites.map((row) => (
                          <tr key={row.date}>
                            <td>{row.date}</td>
                            <td>{row.totalPages.toLocaleString()}</td>
                            <td>{row.sitesCrawled}</td>
                            <td>{row.avgPagesPerSite}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          )}

          {/* Summary charts: total pages, sites crawled, avg pages per site */}
          {dailyStatsAllSites.length > 0 && (
            <Row>
              <Col md={4}>
                <Card className="mb-3">
                  <Card.Header>
                    <Card.Title as="h6" className="mb-0">Total Pages Crawled (per day)</Card.Title>
                  </Card.Header>
                  <Card.Body>
                    <div style={{ height: 200 }}>
                      <Line
                        data={{
                          labels: dailyStatsAllSites.map((d) => d.date),
                          datasets: [{
                            label: "Total pages",
                            data: dailyStatsAllSites.map((d) => d.totalPages),
                            borderColor: "rgb(54, 162, 235)",
                            tension: 0.2,
                          }],
                        }}
                        options={{
                          responsive: true,
                          maintainAspectRatio: false,
                          scales: {
                            y: { beginAtZero: true },
                            x: { ticks: { maxRotation: 45, maxTicksLimit: 10 } },
                          },
                        }}
                      />
                    </div>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={4}>
                <Card className="mb-3">
                  <Card.Header>
                    <Card.Title as="h6" className="mb-0">Sites Crawled (per day)</Card.Title>
                  </Card.Header>
                  <Card.Body>
                    <div style={{ height: 200 }}>
                      <Line
                        data={{
                          labels: dailyStatsAllSites.map((d) => d.date),
                          datasets: [{
                            label: "Sites crawled",
                            data: dailyStatsAllSites.map((d) => d.sitesCrawled),
                            borderColor: "rgb(75, 192, 192)",
                            tension: 0.2,
                          }],
                        }}
                        options={{
                          responsive: true,
                          maintainAspectRatio: false,
                          scales: {
                            y: { beginAtZero: true },
                            x: { ticks: { maxRotation: 45, maxTicksLimit: 10 } },
                          },
                        }}
                      />
                    </div>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={4}>
                <Card className="mb-3">
                  <Card.Header>
                    <Card.Title as="h6" className="mb-0">Avg Pages per Site (per day)</Card.Title>
                  </Card.Header>
                  <Card.Body>
                    <div style={{ height: 200 }}>
                      <Line
                        data={{
                          labels: dailyStatsAllSites.map((d) => d.date),
                          datasets: [{
                            label: "Avg pages/site",
                            data: dailyStatsAllSites.map((d) => d.avgPagesPerSite),
                            borderColor: "rgb(255, 159, 64)",
                            tension: 0.2,
                          }],
                        }}
                        options={{
                          responsive: true,
                          maintainAspectRatio: false,
                          scales: {
                            y: { beginAtZero: true },
                            x: { ticks: { maxRotation: 45, maxTicksLimit: 10 } },
                          },
                        }}
                      />
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          )}

          {/* Per-site time series (default: All sites) */}
          <Row>
            <Col>
              <Card className="mb-3">
                <Card.Header>
                  <Card.Title as="h5" className="mb-0">Pages by Site (Last ~30 Days)</Card.Title>
                </Card.Header>
                <Card.Body>
                  <Form.Group className="mb-2">
                    <Form.Label className="small">Site</Form.Label>
                    <Form.Control
                      as="select"
                      value={timeSeriesSiteId}
                      onChange={(e) => setTimeSeriesSiteId(e.target.value)}
                      size="sm"
                      style={{ maxWidth: 400 }}
                    >
                      <option value={ALL_SITES_VALUE}>All sites (total pages per day)</option>
                      {timeSeriesSiteIds.map((id) => (
                        <option key={id} value={id}>{siteIdToName[id] || id}</option>
                      ))}
                    </Form.Control>
                  </Form.Group>
                  <div style={{ height: 260 }}>
                    {timeSeriesChartData ? (
                      <Line
                        data={timeSeriesChartData}
                        options={{
                          responsive: true,
                          maintainAspectRatio: false,
                          scales: {
                            y: { beginAtZero: true, title: { display: true, text: "Pages" } },
                            x: { title: { display: true, text: "Date" } },
                          },
                        }}
                      />
                    ) : (
                      <div className="text-muted small">No time series for this site.</div>
                    )}
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </>
      )}

      {/* Placeholder for future: Last 30 Days or other metrics when API supports it */}
      {rawData && typeof rawData.last_30_days !== "undefined" && (
        <Row className="mt-3">
          <Col lg={12}>
            <Card>
              <Card.Header>
                <Card.Title as="h4">Last 30 Days</Card.Title>
              </Card.Header>
              <Card.Body>
                <pre className="small">{JSON.stringify(rawData.last_30_days, null, 2)}</pre>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}
    </Container>
  );
}

export default AdminData;
